import { broadcastQueryClient } from '@tanstack/query-broadcast-client-experimental';

const SYNC_REGISTRY_KEY = '__qflowTanstackQueryBroadcastRegistry';

const isBrowserRuntime = () => (
  typeof window !== 'undefined'
  && typeof document !== 'undefined'
);

const isCrossTabSyncDebugEnabled = () => {
  if (!isBrowserRuntime()) return false;
  try {
    return window.localStorage?.getItem('qflowDebugCrossTabSync') === '1';
  } catch (_error) {
    return false;
  }
};

const logCrossTabSyncDebug = (message, payload = null) => {
  if (!isCrossTabSyncDebugEnabled()) return;
  if (payload === null) {
    console.log(`[QueryCrossTabSync] ${message}`);
    return;
  }
  console.log(`[QueryCrossTabSync] ${message}`, payload);
};

const sanitizeChannelSegment = (value) => (
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '-')
);

export const getDefaultQueryCrossTabChannel = (options = {}) => {
  const stage = sanitizeChannelSegment(options?.stage || process.env.REACT_APP_STAGE || 'local');
  const host = sanitizeChannelSegment(
    options?.host
      || (isBrowserRuntime() ? window.location.host : 'unknown')
  );
  return `qflow-rq-v5-${stage}-${host}`;
};

export const initQueryCrossTabSync = (queryClient, options = {}) => {
  if (!queryClient || !isBrowserRuntime()) {
    return () => {};
  }

  if (typeof window.BroadcastChannel === 'undefined') {
    logCrossTabSyncDebug('BroadcastChannel is unavailable; skipping cross-tab query sync.');
    return () => {};
  }

  const broadcastChannel = options?.broadcastChannel || getDefaultQueryCrossTabChannel(options);
  if (!window[SYNC_REGISTRY_KEY] || typeof window[SYNC_REGISTRY_KEY] !== 'object') {
    window[SYNC_REGISTRY_KEY] = {};
  }

  const registry = window[SYNC_REGISTRY_KEY];
  const existingRegistration = registry[broadcastChannel];
  if (
    existingRegistration
    && existingRegistration.queryClient === queryClient
    && typeof existingRegistration.cleanup === 'function'
  ) {
    logCrossTabSyncDebug('Reusing existing broadcast registration.', { broadcastChannel });
    return existingRegistration.cleanup;
  }

  if (existingRegistration && typeof existingRegistration.cleanup === 'function') {
    try {
      existingRegistration.cleanup();
      logCrossTabSyncDebug('Disposed stale broadcast registration before re-registering.', { broadcastChannel });
    } catch (error) {
      console.error('[QueryCrossTabSync] Failed disposing stale broadcast registration.', error);
    }
  }

  let cleanup = () => {};
  try {
    const maybeCleanup = broadcastQueryClient({
      queryClient,
      broadcastChannel,
      options: options?.broadcastOptions,
    });
    cleanup = typeof maybeCleanup === 'function' ? maybeCleanup : () => {};
    logCrossTabSyncDebug('Initialized broadcast query sync.', { broadcastChannel });
  } catch (error) {
    console.error('[QueryCrossTabSync] Failed to initialize cross-tab sync.', error);
    return () => {};
  }

  const wrappedCleanup = () => {
    try {
      cleanup();
    } catch (error) {
      console.error('[QueryCrossTabSync] Failed while cleaning up broadcast sync.', error);
    } finally {
      if (registry[broadcastChannel]?.queryClient === queryClient) {
        delete registry[broadcastChannel];
      }
      logCrossTabSyncDebug('Cleaned up broadcast query sync.', { broadcastChannel });
    }
  };

  registry[broadcastChannel] = {
    queryClient,
    cleanup: wrappedCleanup,
  };

  return wrappedCleanup;
};
